import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Chart from 'react-apexcharts';
import { BarChart3, Home } from 'lucide-react';

import RoomModal from './RoomModal';

const FloorPlan = () => {
  const { floorNumber } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rooms, setRooms] = useState([]);
  const [allRooms, setAllRooms] = useState([]);
  const [floorStats, setFloorStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showModal, setShowModal] = useState(false);
  // URL'den tab parametresini oku, yoksa varsayılan olarak 'overview'
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');

  const fetchRooms = useCallback(async () => {
    try {
      // Cache'i tamamen temizlemek için timestamp ve random parametre ekle
      const timestamp = new Date().getTime();
      const random = Math.random();
      const response = await axios.get(`/api/rooms/floor/${floorNumber}?t=${timestamp}&r=${random}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      setRooms(response.data);
    } catch (error) {
      console.error('Oda bilgileri alınamadı:', error);
      toast.error('Oda bilgileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [floorNumber]);

  const fetchAllData = useCallback(async () => {
    try {
      const [allRoomsResponse, floorStatsResponse] = await Promise.all([
        axios.get('/api/rooms'),
        axios.get('/api/rooms/stats/floors')
      ]);
      setAllRooms(allRoomsResponse.data);
      setFloorStats(floorStatsResponse.data);
    } catch (error) {
      console.error('Veriler alınamadı:', error);
    }
  }, []);

  useEffect(() => {
    // URL'den tab parametresini kontrol et
    const tabParam = searchParams.get('tab');
    if (tabParam === 'rooms' || tabParam === 'overview') {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchRooms();
    fetchAllData();
    // Gerçek zamanlı güncelleme için interval
    const interval = setInterval(() => {
      fetchRooms();
      fetchAllData();
    }, 30000); // 30 saniyede bir güncelle
    return () => clearInterval(interval);
  }, [floorNumber, fetchRooms, fetchAllData]);

  const handleRoomClick = (room) => {
    setSelectedRoom(room);
    setShowModal(true);
  };

  const getRoomColor = (room) => {
    switch (room.status_color) {
      case 'green':
        return '#2e7d32'; // Boş
      case 'orange':
        return '#f59e0b'; // Son 1 kişi
      case 'gray':
        return '#6b7280'; // Dolu
      case 'yellow':
        return '#eab308'; // Kısmen dolu
      default:
        return '#e5e7eb';
    }
  };

  const getRoomTextColor = (room) => {
    if (room.status_color === 'green') {
      return '#ffffff'; // Boş odalar için beyaz yazı
    }
    return room.status_color === 'gray' ? '#ffffff' : '#1f2937';
  };

  // Grafik verileri için hesaplamalar
  const calculateChartData = () => {
    if (!allRooms.length || !floorStats.length) {
      return {
        floorOccupancyData: [1, 2, 3, 4, 5, 6].map(floor => ({
          floor,
          occupancyRate: 0,
          totalRooms: 10,
          occupiedRooms: 0,
          availableBeds: 20
        })),
        roomStatusData: {
          empty: 0,
          partial: 0,
          almostFull: 0,
          full: 0
        },
        averageOccupancyRate: 0
      };
    }

    const floorOccupancyData = floorStats.map(floor => ({
      floor: floor.floor,
      occupancyRate: floor.occupancy_rate,
      totalRooms: floor.total_rooms,
      occupiedRooms: floor.total_occupied,
      availableBeds: floor.available_beds
    }));

    const roomStatusData = {
      empty: allRooms.filter(r => r.status_color === 'green').length,
      partial: allRooms.filter(r => r.status_color === 'yellow').length,
      almostFull: allRooms.filter(r => r.status_color === 'orange').length,
      full: allRooms.filter(r => r.status_color === 'gray').length
    };

    const totalCapacity = allRooms.reduce((sum, room) => sum + room.capacity, 0);
    const totalOccupied = allRooms.reduce((sum, room) => sum + room.occupied, 0);
    const averageOccupancyRate = totalCapacity > 0 ? Math.round((totalOccupied / totalCapacity) * 100) : 0;

    return {
      floorOccupancyData,
      roomStatusData,
      averageOccupancyRate
    };
  };

  const chartData = calculateChartData();

  // Kat planı düzeni - her kat için 10 oda (5x2 düzen)
  const getFloorLayout = () => {
    const baseLayout = {
      width: 800,
      height: 400,
      rooms: []
    };

    // Her kat için 10 oda oluştur (5x2 düzen)
    const floorPrefix = floorNumber;
    for (let row = 0; row < 2; row++) {
      for (let col = 0; col < 5; col++) {
        const roomNumber = col + 1 + (row * 5);
        const roomId = `${floorPrefix}${roomNumber.toString().padStart(2, '0')}`;
        
        baseLayout.rooms.push({
          x: 50 + (col * 150),
          y: 50 + (row * 120),
          width: 120,
          height: 80,
          number: roomId
        });
      }
    }
    
    return baseLayout;
  };

  const layout = getFloorLayout();

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">Yükleniyor...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto !py-8">
      {/* Üst Tab Menü - Ortada Yatay */}
      <div className="mb-6 flex justify-start">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-1 inline-flex">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex items-center space-x-2 px-6 py-3 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'overview'
                ? 'bg-primary text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <BarChart3 className="w-5 h-5" />
            <span>Genel Görünüm</span>
          </button>
          <button
            onClick={() => setActiveTab('rooms')}
            className={`flex items-center space-x-2 px-6 py-3 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'rooms'
                ? 'bg-primary text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Home className="w-5 h-5" />
            <span>Odalar</span>
          </button>
        </div>
      </div>

      {/* İçerik Alanı */}
      <div>
          {/* Genel Görünüm Tab - İstatistikler ve Grafikler */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
              </div>

              {/* Ortalama Doluluk Oranı */}
              <div className="card">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Ortalama Doluluk Oranı</h3>
                <div className="text-center py-6">
                  <div className="text-5xl font-bold text-primary mb-2">
                    {chartData?.averageOccupancyRate || 0}%
                  </div>
                  <p className="text-gray-600">
                    {allRooms.reduce((sum, r) => sum + r.occupied, 0)} / {allRooms.length > 0 ? allRooms.reduce((sum, r) => sum + r.capacity, 0) : 0} öğrenci
                  </p>
                </div>
              </div>

              {/* Grafikler Grid */}
              {chartData && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Kat Bazında Doluluk Oranları */}
                  <div className="card">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Kat Bazında Doluluk Oranları</h3>
                    <Chart
                      options={{
                        chart: {
                          type: 'bar',
                          toolbar: { show: false }
                        },
                        plotOptions: {
                          bar: {
                            horizontal: false,
                            columnWidth: '55%',
                            borderRadius: 4
                          }
                        },
                        dataLabels: {
                          enabled: true,
                          formatter: (val) => `${val}%`
                        },
                        xaxis: {
                          categories: chartData.floorOccupancyData.map(f => `${f.floor}. Kat`),
                          title: { text: 'Katlar' }
                        },
                        yaxis: {
                          title: { text: 'Doluluk Oranı (%)' },
                          max: 100
                        },
                        colors: ['#043132'],
                        fill: {
                          opacity: 0.8
                        },
                        tooltip: {
                          y: {
                            formatter: (val) => `${val}%`
                          }
                        }
                      }}
                      series={[{
                        name: 'Doluluk Oranı',
                        data: chartData.floorOccupancyData.map(f => f.occupancyRate)
                      }]}
                      type="bar"
                      height={300}
                    />
                  </div>

                  {/* Oda Durumları */}
                  <div className="card">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Oda Durumları</h3>
                    <Chart
                      options={{
                        chart: {
                          type: 'pie',
                          toolbar: { show: false }
                        },
                        labels: ['Boş', 'Kısmen Dolu', 'Son 1 Kişi', 'Dolu'],
                        colors: ['#2e7d32', '#eab308', '#f59e0b', '#6b7280'],
                        legend: {
                          position: 'bottom'
                        },
                        dataLabels: {
                          enabled: true,
                          formatter: (val) => `${val.toFixed(1)}%`
                        },
                        tooltip: {
                          y: {
                            formatter: (val) => `${val} oda`
                          }
                        }
                      }}
                      series={[
                        chartData.roomStatusData.empty,
                        chartData.roomStatusData.partial,
                        chartData.roomStatusData.almostFull,
                        chartData.roomStatusData.full
                      ]}
                      type="pie"
                      height={300}
                    />
                  </div>
                </div>
              )}

              {/* Detaylı İstatistikler */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="card text-center">
                  <div className="text-2xl font-bold text-primary mb-1">
                    {chartData?.floorOccupancyData?.reduce((sum, f) => sum + f.availableBeds, 0) || 0}
                  </div>
                  <div className="text-xs text-gray-600">Toplam Boş Yatak</div>
                </div>
                <div className="card text-center">
                  <div className="text-2xl font-bold text-orange-600 mb-1">
                    {chartData?.roomStatusData?.almostFull || 0}
                  </div>
                  <div className="text-xs text-gray-600">Son 1 Kişi</div>
                </div>
                <div className="card text-center">
                  <div className="text-2xl font-bold text-yellow-600 mb-1">
                    {chartData?.roomStatusData?.partial || 0}
                  </div>
                  <div className="text-xs text-gray-600">Kısmen Dolu</div>
                </div>
                <div className="card text-center">
                  <div className="text-2xl font-bold text-gray-600 mb-1">
                    {chartData?.roomStatusData?.full || 0}
                  </div>
                  <div className="text-xs text-gray-600">Tam Dolu</div>
                </div>
              </div>
            </div>
          )}

          {/* Odalar Tab - Kat Planı */}
          {activeTab === 'rooms' && (
            <div>
              {/* Kat Seçimi */}
              <div className="mb-6">
                <div className="flex flex-wrap gap-2 justify-center">
                  {[1, 2, 3, 4, 5, 6].map((floor) => (
                    <button
                      key={floor}
                      onClick={() => navigate(`/floor/${floor}`)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        parseInt(floorNumber) === floor
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {floor}. Kat
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6 flex justify-between items-center">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {floorNumber}. Kat Planı
                  </h1>
                  <p className="text-gray-600">
                    Odalara tıklayarak detayları görüntüleyebilir ve başvuru yapabilirsiniz
                  </p>
                </div>
                <button
                  onClick={fetchRooms}
                  disabled={loading}
                  className="bg-primary hover:bg-primary-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 disabled:opacity-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Yenile</span>
                </button>
              </div>

              {/* Durum Açıklaması */}
              <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: '#2e7d32' }}></div>
                  <span className="text-sm text-gray-600">Boş</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                  <span className="text-sm text-gray-600">Kısmen Dolu</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-orange-500 rounded"></div>
                  <span className="text-sm text-gray-600">Son 1 Kişi</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-gray-500 rounded"></div>
                  <span className="text-sm text-gray-600">Dolu</span>
                </div>
              </div>

              {/* Kat Planı */}
              <div className="bg-white rounded-lg shadow-lg p-6 overflow-auto">
                <svg
                  width={layout.width}
                  height={layout.height}
                  viewBox={`0 0 ${layout.width} ${layout.height}`}
                  className="mx-auto"
                >
                  {/* Arka plan */}
                  <rect
                    width={layout.width}
                    height={layout.height}
                    fill="#f8fafc"
                    stroke="#e2e8f0"
                    strokeWidth="2"
                  />

                  {/* Koridor */}
                  <rect
                    x="50"
                    y="120"
                    width="680"
                    height="20"
                    fill="#e2e8f0"
                    stroke="#cbd5e1"
                    strokeWidth="1"
                    className="hidden"
                  />
                  <text
                    x="390"
                    y="135"
                    textAnchor="middle"
                    className="text-sm font-medium fill-gray-600 hidden"
                  >
                    Koridor
                  </text>

                  {/* Odalar */}
                  {layout.rooms.map((roomLayout) => {
                    const room = rooms.find(r => r.room_number === roomLayout.number);
                    const color = room ? getRoomColor(room) : '#e5e7eb';
                    const textColor = room ? getRoomTextColor(room) : '#6b7280';
                    
                    return (
                      <g key={roomLayout.number}>
                        <rect
                          x={roomLayout.x}
                          y={roomLayout.y}
                          width={roomLayout.width}
                          height={roomLayout.height}
                          fill={color}
                          stroke="#374151"
                          strokeWidth="1"
                          className="cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => room && handleRoomClick(room)}
                        />
                        <text
                          x={roomLayout.x + roomLayout.width / 2}
                          y={roomLayout.y + roomLayout.height / 2}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="text-xs font-medium"
                          fill={textColor}
                        >
                          {roomLayout.number}
                        </text>
                        {room && (
                          <text
                            x={roomLayout.x + roomLayout.width / 2}
                            y={roomLayout.y + roomLayout.height / 2 + 15}
                            textAnchor="middle"
                            className="text-xs"
                            fill={textColor}
                          >
                            {room.occupied}/{room.capacity}
                          </text>
                        )}
                      </g>
                    );
                  })}

                  {/* Merdiven */}
                  <rect
                    x="750"
                    y="50"
                    width="40"
                    height="200"
                    fill="#fbbf24"
                    stroke="#f59e0b"
                    strokeWidth="2"
                    className="hidden"
                  />
                  <text
                    x="770"
                    y="150"
                    textAnchor="middle"
                    className="text-xs font-medium hidden fill-amber-800"
                  >
                    Merdiven
                  </text>
                </svg>
              </div>

              {/* İstatistikler */}
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="card text-center">
                  <div className="text-2xl font-bold text-primary">{rooms.length}</div>
                  <div className="text-sm text-gray-600">Toplam Oda</div>
                </div>
                <div className="card text-center">
                  <div className="text-2xl font-bold" style={{ color: '#2e7d32' }}>
                    {rooms.filter(r => r.status_color === 'green').length}
                  </div>
                  <div className="text-sm text-gray-600">Boş Oda</div>
                </div>
                <div className="card text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {rooms.filter(r => r.status_color === 'orange').length}
                  </div>
                  <div className="text-sm text-gray-600">Son 1 Kişi</div>
                </div>
                <div className="card text-center">
                  <div className="text-2xl font-bold text-gray-600">
                    {rooms.filter(r => r.status_color === 'gray').length}
                  </div>
                  <div className="text-sm text-gray-600">Dolu Oda</div>
                </div>
              </div>
            </div>
          )}
      </div>

      {/* Oda Modal */}
      {showModal && selectedRoom && (
        <RoomModal
          room={selectedRoom}
          onClose={() => {
            setShowModal(false);
            setSelectedRoom(null);
          }}
          onApplicationSubmit={() => {
            fetchRooms(); // Başvuru sonrası odaları yenile
          }}
        />
      )}
    </div>
  );
};

export default FloorPlan; 